import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  Wrench, Plus, Trash2, Loader2, ExternalLink, Eye, X, ShieldCheck,
  Github, Cloud, Factory, Slack, Notebook, Kanban, Globe, Check, Link2
} from "lucide-react";

const ICONS = { github: Github, cloud: Cloud, factory: Factory, slack: Slack, notebook: Notebook, kanban: Kanban };

/**
 * /workforce/tools — MCP Tool Registry. First-party presets (Notion, GitHub,
 * Jira, Salesforce, SAP, Slack) + generic HTTP. Saved tools are usable from any
 * Workflow Tool node via tool="mcp".
 */
export default function ToolRegistry() {
  const [presets, setPresets] = useState([]);
  const [tools, setTools] = useState([]);
  const [oauth, setOauth] = useState({});
  const [active, setActive] = useState(null); // preset being configured, or "custom"
  const [preview, setPreview] = useState(null);
  const [params, setParams] = useSearchParams();
  const banner = params.get("connected") ? { ok: true, text: `${params.get("connected")} connected — you can now create tools without pasting a token.` }
    : params.get("oauth_error") ? { ok: false, text: `OAuth failed: ${params.get("oauth_error")}` } : null;

  const load = useCallback(() => {
    Promise.all([
      api.get("/mcp_presets").then((r) => r.data.presets),
      api.get("/mcp_tools").then((r) => r.data.tools),
      api.get("/oauth/providers").then((r) => r.data.providers).catch(() => ({})),
    ]).then(([p, t, o]) => { setPresets(p); setTools(t); setOauth(o); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const disconnect = async (pid) => {
    if (!window.confirm(`Disconnect ${oauth[pid]?.label}? Existing tools keep working; new ones will need a token.`)) return;
    await api.delete(`/oauth/${pid}`);
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tool "${t.name}"? Workflow nodes referencing it will fail.`)) return;
    await api.delete(`/mcp_tools/${t.tool_id}`);
    load();
  };

  const showPreview = async (t) => {
    const { data } = await api.post(`/mcp_tools/${t.tool_id}/preview`, { sample_input: "Quarterly revenue summary" });
    setPreview({ tool: t, ...data });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          <Wrench size={12} strokeWidth={2.5} /> MCP Tool Registry
        </div>
        <h1 className="font-heading text-4xl font-bold" data-testid="tools-title">Connect your stack</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Give workflows hands. Each connector becomes a saved tool your <b>Tool</b> nodes can call — credentials are encrypted at rest and never returned to the browser.
        </p>
      </div>

      {banner && (
        <div className={`brut-card p-3 text-sm flex items-center justify-between ${banner.ok ? "bg-emerald-100" : "bg-red-100"}`} data-testid="oauth-banner">
          <span>{banner.text}</span>
          <button onClick={() => setParams({})} className="text-slate-500 hover:text-slate-900"><X size={14} /></button>
        </div>
      )}

      {/* Catalogue */}
      <section>
        <h2 className="font-heading text-lg font-bold mb-3">Connectors</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="preset-grid">
          {presets.map((p) => {
            const Icon = ICONS[p.icon] || Globe;
            const count = tools.filter((t) => t.preset_id === p.preset_id).length;
            const o = oauth[p.preset_id];
            return (
              <button key={p.preset_id} onClick={() => setActive(p)}
                      className="brut-card p-4 text-left hover:-translate-y-0.5 transition-transform"
                      style={{ boxShadow: `4px 4px 0 ${p.color}` }}
                      data-testid={`preset-${p.preset_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-xl border-2 border-slate-900 flex items-center justify-center text-white" style={{ background: p.color }}>
                    <Icon size={18} strokeWidth={2.5} />
                  </div>
                  <div className="flex gap-1">
                    {o?.connected && <span className="text-[10px] font-bold bg-sky-200 border border-slate-900 rounded-full px-2 py-0.5" data-testid={`oauth-badge-${p.preset_id}`}><Link2 size={9} className="inline mr-0.5" />@{o.connection?.account_name}</span>}
                    {count > 0 && <span className="text-[10px] font-bold bg-emerald-300 border border-slate-900 rounded-full px-2 py-0.5">{count} connected</span>}
                  </div>
                </div>
                <div className="font-heading font-bold">{p.provider}</div>
                <div className="text-xs text-slate-600 mt-1 leading-snug">{p.tagline}</div>
              </button>
            );
          })}
          <button onClick={() => setActive("custom")} className="brut-card p-4 text-left border-dashed hover:-translate-y-0.5 transition-transform" data-testid="preset-custom">
            <div className="w-10 h-10 rounded-xl border-2 border-slate-900 bg-slate-100 flex items-center justify-center mb-2"><Globe size={18} strokeWidth={2.5} /></div>
            <div className="font-heading font-bold">Custom HTTP</div>
            <div className="text-xs text-slate-600 mt-1 leading-snug">Any REST endpoint — Zapier, Make, n8n, your own API.</div>
          </button>
        </div>
      </section>

      {/* Saved tools */}
      <section>
        <h2 className="font-heading text-lg font-bold mb-3">Saved tools <span className="text-slate-400 font-mono text-sm">({tools.length})</span></h2>
        {tools.length === 0 ? (
          <div className="brut-card p-6 text-center text-slate-500 italic" data-testid="tools-empty">No tools yet — pick a connector above.</div>
        ) : (
          <div className="space-y-2" data-testid="tools-list">
            {tools.map((t) => (
              <div key={t.tool_id} className="brut-card p-3 flex items-center gap-3 flex-wrap" data-testid={`tool-${t.tool_id}`}>
                <span className="text-[10px] uppercase tracking-widest font-bold bg-slate-900 text-white rounded-full px-2 py-0.5">{t.provider}</span>
                {t.auth_source === "oauth" && <span className="text-[10px] font-bold bg-sky-200 border border-slate-900 rounded-full px-2 py-0.5" data-testid={`tool-oauth-${t.tool_id}`}>OAuth</span>}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm truncate" data-testid={`tool-name-${t.tool_id}`}>{t.name}</div>
                  <div className="text-[11px] font-mono text-slate-500 truncate">{t.method} {t.url}</div>
                </div>
                <code className="text-[10px] font-mono text-slate-400">{t.tool_id}</code>
                <button onClick={() => showPreview(t)} className="brut-btn text-xs bg-white text-slate-900" data-testid={`tool-preview-${t.tool_id}`}>
                  <Eye size={12} strokeWidth={2.5} /> Preview
                </button>
                <button onClick={() => remove(t)} className="text-red-500 hover:text-red-700" title="Delete" data-testid={`tool-delete-${t.tool_id}`}>
                  <Trash2 size={14} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {active && (
        <ToolModal preset={active === "custom" ? null : active} oauth={active === "custom" ? null : oauth[active.preset_id]}
                   onDisconnect={() => disconnect(active.preset_id)} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} />
      )}
      {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function ToolModal({ preset, oauth, onDisconnect, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [fields, setFields] = useState({});
  const [actionId, setActionId] = useState(preset?.actions?.[0]?.action_id || "");
  const [name, setName] = useState("");
  const [custom, setCustom] = useState({ method: "POST", url: "", headers_json: "{}", body_template: '{"content":"{{workflow.input}}"}' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const useOauth = !!oauth?.connected;

  const startOauth = async () => {
    try {
      const { data } = await api.get(`/oauth/${preset.preset_id}/start`);
      window.location.assign(data.authorize_url);
    } catch (e) { setErr(e.response?.data?.detail || "Could not start OAuth"); }
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (preset) {
        await api.post("/mcp_tools/from-preset", { preset_id: preset.preset_id, action_id: actionId, name, fields });
      } else {
        await api.post("/mcp_tools", { name: name || "Custom HTTP tool", ...custom });
      }
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map((x) => x.msg).join("; ") : (d || "Save failed"));
    } finally { setBusy(false); }
  };

  const action = preset?.actions?.find((a) => a.action_id === actionId);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="brut-card bg-white w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tool-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold">{preset ? `Connect ${preset.provider}` : "Custom HTTP tool"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="tool-modal-close"><X size={18} /></button>
        </div>
        {preset?.docs_url && (
          <a className="text-xs underline text-slate-500 inline-flex items-center gap-1" href={preset.docs_url} target="_blank" rel="noreferrer">
            {preset.provider} API docs <ExternalLink size={10} />
          </a>
        )}

        <Field label="Tool name">
          <input className="brut-input w-full" placeholder={preset ? `${preset.provider} · ${action?.label || ""}` : "e.g. Zapier catch hook"}
                 value={name} onChange={(e) => setName(e.target.value)} data-testid="tool-name-input" />
        </Field>

        {preset ? (
          <>
            {preset.actions.length > 1 && (
              <Field label="Action">
                <select className="brut-input w-full" value={actionId} onChange={(e) => setActionId(e.target.value)} data-testid="tool-action">
                  {preset.actions.map((a) => <option key={a.action_id} value={a.action_id}>{a.label}</option>)}
                </select>
              </Field>
            )}
            {oauth && (
              <div className={`rounded-xl border-2 border-slate-900 p-3 text-xs space-y-2 ${useOauth ? "bg-sky-50" : "bg-slate-50"}`} data-testid="oauth-panel">
                {useOauth ? (
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1 font-bold"><Link2 size={12} strokeWidth={2.5} /> Connected as @{oauth.connection?.account_name} — token filled automatically</span>
                    <button onClick={onDisconnect} className="text-red-500 hover:text-red-700 font-bold" data-testid="oauth-disconnect">Disconnect</button>
                  </div>
                ) : oauth.configured ? (
                  <div className="flex items-center justify-between gap-2">
                    <span>Skip pasting a token — sign in once.</span>
                    <button onClick={startOauth} className="brut-btn text-xs" data-testid="oauth-connect">
                      <Link2 size={12} strokeWidth={2.5} /> Connect {oauth.label}
                    </button>
                  </div>
                ) : (
                  <div data-testid="oauth-not-configured">
                    <span className="font-bold">One-click sign-in isn't available yet.</span> Paste a token below instead.
                    {isAdmin && <> Admin: set <code>{oauth.env_keys[0]}</code> / <code>{oauth.env_keys[1]}</code> in the backend environment (create an app at <a className="underline" href={oauth.setup_url} target="_blank" rel="noreferrer">{oauth.setup_url.replace("https://", "")}</a> with callback <code className="break-all">{oauth.redirect_uri}</code>).</>}
                  </div>
                )}
              </div>
            )}
            {preset.fields.filter((f) => !(useOauth && f.key === "token")).map((f) => (
              <Field key={f.key} label={f.label} required help={f.help}>
                <input type={f.secret ? "password" : "text"} className="brut-input w-full font-mono text-sm" placeholder={f.placeholder}
                       value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                       autoComplete="off" data-testid={`tool-field-${f.key}`} />
              </Field>
            ))}
            {action && (
              <div className="text-[11px] font-mono bg-slate-50 border-2 border-slate-200 rounded-lg p-2 break-all">
                <span className="font-bold">{action.method}</span> {action.url}
                <div className="text-slate-500 mt-1">{action.body}</div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2">
              <select className="brut-input" value={custom.method} onChange={(e) => setCustom({ ...custom, method: e.target.value })} data-testid="custom-method">
                <option>POST</option><option>GET</option>
              </select>
              <input className="brut-input col-span-3 font-mono text-sm" placeholder="https://hooks.example.com/…" value={custom.url}
                     onChange={(e) => setCustom({ ...custom, url: e.target.value })} data-testid="custom-url" />
            </div>
            <Field label="Headers (JSON)">
              <textarea rows={2} className="brut-input w-full font-mono text-xs" value={custom.headers_json}
                        onChange={(e) => setCustom({ ...custom, headers_json: e.target.value })} data-testid="custom-headers" />
            </Field>
            <Field label="Body template (JSON, supports {{workflow.input}})">
              <textarea rows={3} className="brut-input w-full font-mono text-xs" value={custom.body_template}
                        onChange={(e) => setCustom({ ...custom, body_template: e.target.value })} data-testid="custom-body" />
            </Field>
          </>
        )}

        {err && <div className="text-sm text-red-600" data-testid="tool-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="brut-btn text-sm bg-white text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy || (!preset && !custom.url)} className="brut-btn text-sm disabled:opacity-40" data-testid="tool-save">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} strokeWidth={3} />} Save tool
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ preview, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="brut-card bg-white w-full max-w-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold">Dry run · {preview.tool.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="preview-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-500">What a Tool node would send with input <i>"Quarterly revenue summary"</i>. Nothing was called.</p>
        <pre className="text-[11px] font-mono bg-slate-900 text-emerald-200 rounded-lg p-3 overflow-x-auto" data-testid="preview-body">
{`${preview.method} ${preview.url}\n${Object.entries(preview.headers || {}).map(([k, v]) => `${k}: ${v}`).join("\n")}\n\n${JSON.stringify(preview.payload, null, 2)}`}
        </pre>
        <div className={`text-xs inline-flex items-center gap-1 font-bold ${preview.body_is_valid_json ? "text-emerald-600" : "text-amber-600"}`}>
          <Check size={12} strokeWidth={3} /> {preview.body_is_valid_json ? "Body renders as valid JSON" : "Body is not valid JSON — will be sent as {content: …}"}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, help, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">
        {label}{required && <span className="text-red-500">*</span>}
      </div>
      {children}
      {help && <p className="text-[11px] text-slate-500 mt-1">{help}</p>}
    </div>
  );
}
